import { useState, useEffect } from "react";
import { CheckIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  PAGE_SIZE_PRESETS,
  ARCHITECTURAL_PRESETS,
  detectPageSize,
  type PageSizePreset,
  type PresetScale,
} from "@/lib/geometry";
import { cn } from "@/lib/utils";

interface PresetCalibrationPanelProps {
  pagePixelWidth: number;
  pagePixelHeight: number;
  onApplyPreset: (preset: PresetScale, pageWidthInches: number) => void;
  onCancel: () => void;
}

export function PresetCalibrationPanel({
  pagePixelWidth,
  pagePixelHeight,
  onApplyPreset,
  onCancel,
}: PresetCalibrationPanelProps) {
  const [selectedPageSize, setSelectedPageSize] = useState<PageSizePreset | null>(null);
  const [customWidthInches, setCustomWidthInches] = useState<string>("");
  const [selectedScale, setSelectedScale] = useState<PresetScale | null>(null);
  const [showCustomWidth, setShowCustomWidth] = useState(false);

  useEffect(() => {
    const detected = detectPageSize(pagePixelWidth, pagePixelHeight, 2);
    if (detected) {
      setSelectedPageSize(detected);
      setShowCustomWidth(false);
    } else {
      setShowCustomWidth(true);
      const widthInches = (pagePixelWidth / 72).toFixed(2);
      setCustomWidthInches(widthInches);
    }
  }, [pagePixelWidth, pagePixelHeight]);

  const effectiveWidthInches = selectedPageSize
    ? selectedPageSize.widthInches
    : parseFloat(customWidthInches) || 0;

  const canApply = selectedScale && effectiveWidthInches > 0;

  const handleApply = () => {
    if (selectedScale && effectiveWidthInches > 0) {
      onApplyPreset(selectedScale, effectiveWidthInches);
    }
  };

  const handlePageSizeSelect = (preset: PageSizePreset) => {
    setSelectedPageSize(preset);
    setShowCustomWidth(false);
  };

  const handleUseCustomWidth = () => {
    setSelectedPageSize(null);
    setShowCustomWidth(true);
    setCustomWidthInches((pagePixelWidth / 72).toFixed(2));
  };

  return (
    <div className="pointer-events-auto w-72 rounded-lg border bg-background/95 p-3 shadow-xl backdrop-blur">
      <div className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        Preset Calibration
      </div>

      <div className="mb-3 space-y-1.5">
        <div className="text-xs text-muted-foreground">Page Size</div>
        <div className="flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 flex-1 justify-start text-xs">
                {selectedPageSize ? (
                  <>
                    <CheckIcon className="mr-1 size-3" />
                    {selectedPageSize.label}
                  </>
                ) : showCustomWidth ? (
                  "Custom"
                ) : (
                  "Select size..."
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-64 overflow-y-auto">
              {PAGE_SIZE_PRESETS.map((preset) => (
                <DropdownMenuItem
                  key={preset.label}
                  onClick={() => handlePageSizeSelect(preset)}
                  className={cn(
                    "text-xs",
                    selectedPageSize?.label === preset.label && "bg-muted"
                  )}
                >
                  <CheckIcon
                    className={cn(
                      "mr-2 size-3 opacity-0",
                      selectedPageSize?.label === preset.label && "opacity-100"
                    )}
                  />
                  {preset.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuItem
                onClick={handleUseCustomWidth}
                className={cn("text-xs", showCustomWidth && "bg-muted")}
              >
                <CheckIcon
                  className={cn("mr-2 size-3 opacity-0", showCustomWidth && "opacity-100")}
                />
                Custom dimensions
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {showCustomWidth && (
          <div className="flex items-center gap-1 mt-1">
            <Input
              type="text"
              inputMode="decimal"
              value={customWidthInches}
              onChange={(e) => setCustomWidthInches(e.target.value)}
              className="h-7 w-20 text-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              placeholder="Width"
            />
            <span className="text-xs text-muted-foreground">inches</span>
            <span className="text-xs text-muted-foreground">
              ({pagePixelWidth} pts)
            </span>
          </div>
        )}

        {!showCustomWidth && selectedPageSize && (
          <div className="text-[10px] text-muted-foreground">
            {selectedPageSize.widthInches}" × {selectedPageSize.heightInches}" ({pagePixelWidth} × {pagePixelHeight} pts)
          </div>
        )}
      </div>

      <div className="mb-3 space-y-1.5">
        <div className="text-xs text-muted-foreground">Scale</div>
        <div className="grid grid-cols-2 gap-1">
          {ARCHITECTURAL_PRESETS.map((preset) => (
            <Button
              key={preset.label}
              variant={selectedScale?.label === preset.label ? "default" : "outline"}
              size="sm"
              className="h-7 text-xs"
              onClick={() => setSelectedScale(preset)}
            >
              {preset.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <Button
          size="sm"
          className="h-8 flex-1"
          disabled={!canApply}
          onClick={handleApply}
        >
          Apply
        </Button>
        <Button size="sm" variant="ghost" className="h-8" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
